import React, { createContext, ReactNode, useContext, useState } from "react";
import { TEAM_LIST_VIEW_MODE } from "../constants";

interface IGapMode {
  competencies: boolean;
  tasks: boolean;
  regulations: boolean;
  trainings: boolean;
}

interface IExpandRowIds {
  tasks: string[];
  regulations: string[];
  trainings: string[];
}

interface IKeywords {
  tasks: string;
  regulations: string;
  trainings: string;
}

interface IListViewMode {
  [key: string]: {
    people: boolean;
  };
}
interface ITeamPeoplePagination {
  [key: string]: {
    page: number;
    pageSize: number;
  };
}

interface TeamContextType {
  gapMode: IGapMode;
  setGapMode: (gapMode: IGapMode) => void;
  expandRowIds: IExpandRowIds;
  setExpandRowIds: (expandRowIds: IExpandRowIds) => void;
  keywords: IKeywords;
  setKeywords: (keywords: IKeywords) => void;
  listViewMode: IListViewMode;
  setListViewMode: (listViewMode: IListViewMode) => void;
  resetContextState: () => void;
  onSetListViewMode: (value: IListViewMode) => void;
  teamPeoplePagination: ITeamPeoplePagination;
  onSetTeamPeoplePagination: (value: ITeamPeoplePagination) => void;
}

export const TeamContext = createContext<TeamContextType | undefined>(undefined);

export const useTeamContext = () => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error("useTeamContext must be used within a TeamProvider");
  }
  return context;
};

interface TeamProviderProps {
  children: ReactNode;
}

const defaultGapMode = {
  competencies: false,
  tasks: false,
  regulations: false,
  trainings: false
};

const defaultExpandRowIds = {
  tasks: [],
  regulations: [],
  trainings: []
};

const defaultKeywords = {
  tasks: "",
  regulations: "",
  trainings: ""
};

export const TeamProvider: React.FC<TeamProviderProps> = ({ children }) => {
  const [gapMode, setGapMode] = useState<IGapMode>(defaultGapMode);
  const [expandRowIds, setExpandRowIds] = useState<IExpandRowIds>(defaultExpandRowIds);
  const [keywords, setKeywords] = useState<IKeywords>(defaultKeywords);
  const [listViewMode, setListViewMode] = useState<IListViewMode>(
    localStorage.getItem(TEAM_LIST_VIEW_MODE)
      ? JSON.parse(localStorage.getItem(TEAM_LIST_VIEW_MODE) || "")
      : {}
  );

  const [teamPeoplePagination, setTeamPeoplePagination] = useState<ITeamPeoplePagination>({});

  const resetContextState = () => {
    setGapMode(defaultGapMode);
    setExpandRowIds(defaultExpandRowIds);
    setKeywords(defaultKeywords);
  };

  const onSetListViewMode = (value: IListViewMode) => {
    const mode = {
      ...listViewMode,
      ...value
    };

    setListViewMode(mode);

    localStorage.setItem(TEAM_LIST_VIEW_MODE, JSON.stringify(mode));
  };
  const onSetTeamPeoplePagination = (value: ITeamPeoplePagination) => {
    const pagination = {
      ...teamPeoplePagination,
      ...value
    };

    setTeamPeoplePagination(pagination);
  };

  return (
    <TeamContext.Provider
      value={{
        gapMode,
        setGapMode,
        expandRowIds,
        setExpandRowIds,
        keywords,
        setKeywords,
        listViewMode,
        setListViewMode,
        resetContextState,
        onSetListViewMode,
        teamPeoplePagination,
        onSetTeamPeoplePagination
      }}
    >
      {children}
    </TeamContext.Provider>
  );
};
